import React from "react";
import { motion } from "framer-motion";
import { ShieldAlert, TrendingDown, Landmark, MessageCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

// Four benefit-led product features. Emojis in the source spec are replaced
// with lucide SVGs per the PRD "no emoji in UI" rule.
const FEATURES = [
    { id: "leak",   icon: ShieldAlert,   tint: "#FF3EA5", key: "cf_leak"   },
    { id: "debt",   icon: TrendingDown,  tint: "#0AFFB0", key: "cf_debt"   },
    { id: "dash",   icon: Landmark,      tint: "#B36CFF", key: "cf_dash"   },
    { id: "chat",   icon: MessageCircle, tint: "#22D3EE", key: "cf_chat"   },
];

const CaesarFeatures = () => {
    const { language } = useApp();

    return (
        <section
            id="what-caesar-does"
            className="w-full py-16 md:py-24 bg-[hsl(var(--background))] border-t border-[hsl(var(--border))]"
            data-testid="landing-caesar-features"
        >
            <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-12">
                <div className="text-center mb-12 md:mb-16">
                    <div className="text-[10px] md:text-xs font-bold tracking-[0.28em] text-[hsl(var(--muted-foreground))] uppercase">
                        {t(language, "cf_eyebrow")}
                    </div>
                    <h2 className="mt-3 font-display font-extrabold text-[hsl(var(--primary))] text-3xl md:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1.05]">
                        {t(language, "cf_title")}
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
                    {FEATURES.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <motion.div
                                key={f.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.5, delay: i * 0.08, ease: "easeOut" }}
                                className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 md:p-7 hover:border-[hsl(var(--primary))]/40 hover:shadow-lg transition-all group text-center"
                                data-testid={`cf-item-${i + 1}`}
                            >
                                <div
                                    className="w-14 h-14 mx-auto rounded-2xl grid place-items-center mb-5"
                                    style={{ background: `${f.tint}18`, color: f.tint, boxShadow: `0 0 24px -6px ${f.tint}55` }}
                                >
                                    <Icon className="w-6 h-6" />
                                </div>
                                <h3 className="font-display font-bold text-lg md:text-xl text-[hsl(var(--foreground))] tracking-tight">
                                    {t(language, `${f.key}_title`)}
                                </h3>
                                <p className="mt-2 text-sm md:text-[15px] leading-relaxed text-[hsl(var(--muted-foreground))]">
                                    {t(language, `${f.key}_desc`)}
                                </p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default CaesarFeatures;
